import {
  type TerraIcon,
  HierarchyIcon,
  ReplaceIcon,
  FlashIcon,
  ReplayIcon,
  GitCompareIcon,
  CableIcon,
  CursorPointer01Icon,
  Target03Icon,
  Shapes01Icon,
  Alert01Icon,
  File01Icon,
  PlayIcon,
  SourceCodeIcon,
  FolderLibraryIcon,
  RadioIcon,
  Home03Icon,
  WorkHistoryIcon,
} from '@terra/ui-icons';
import type {
  MainContentTab,
  OpenMainContentTabInput,
  SidebarTab,
} from '../../store/layoutStore';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import {
  targetSupports,
  isAppTarget,
  type TargetCapability,
} from '../../utils/target-scope.utils';

export function resolveOverviewTabTitle({
  tabId,
  title,
  tabType,
  targetType,
  home,
  targetOverview,
}: {
  tabId: string;
  title: string;
  tabType: MainContentTab;
  targetType: TargetTypeType;
  home: string;
  targetOverview: string;
}): string {
  if (tabId !== 'application-overview' || tabType !== 'application') {
    return title;
  }

  return isAppTarget(targetType) ? home : targetOverview;
}

export type SidebarSectionItem = {
  id: SidebarTab;
  icon: TerraIcon;
  label: string;
  count?: number;
  hasNew?: boolean;
  isSubItem?: boolean;
  action?: () => void;
};
type SidebarSection = {
  id: string;
  sectionIcon: TerraIcon;
  sectionLabel: string;
  items: SidebarSectionItem[];
};
type SidebarLabels = {
  sectionDataAndAssets: string;
  sectionTrafficAndMonitoring: string;
  sectionProxyTools: string;
  assets: string;
  code: string;
  signals: string;
  executions: string;
  findings: string;
  reports: string;
  httpHistory: string;
  webSocketSse: string;
  intercept: string;
  matchReplace: string;
  replay: string;
  comparer: string;
  interact: string;
  applicationSettings: string;
  proxySettings: string;
  networkGraph: string;
  targetOverview: string;
  home: string;
};
type SidebarSectionOptions = SidebarLabels & {
  isExternalNetworkEnabled: boolean;
  targetType: TargetTypeType;
  hasApplicationSourceCode?: boolean;
};

export const sidebarTabMapping = {
  dashboard: {
    id: 'application-overview',
    title: 'Home',
    type: 'application',
  },
  signals: {
    id: 'signals-list',
    title: 'Signals',
    type: 'signals-list',
  },
  assets: {
    id: 'assets-list',
    title: 'Assets',
    type: 'assets-list',
  },
  findings: {
    id: 'findings-list',
    title: 'Findings',
    type: 'findings-list',
  },
  reports: {
    id: 'reports-list',
    title: 'Reports',
    type: 'reports-list',
  },
  executions: {
    id: 'executions-list',
    title: 'Executions',
    type: 'executions-list',
  },
  code: {
    id: 'code-list',
    title: 'Code',
    type: 'code-list',
  },
  skills: {
    id: 'skills-list',
    title: 'Skills',
    type: 'skills-list',
  },
  'agent-executions': {
    id: 'agent-executions-list',
    title: 'Agent Executions',
    type: 'agent-executions-list',
  },
  requests: {
    id: 'requests',
    title: 'HTTP History',
    type: 'requests',
  },
  websocket: {
    id: 'websocket',
    title: 'WS / SSE',
    type: 'websocket',
  },
  intercept: {
    id: 'intercept',
    title: 'Intercept',
    type: 'intercept',
  },
  'match-replace': {
    id: 'match-replace',
    title: 'Match & Replace',
    type: 'match-replace',
  },
  replay: {
    id: 'replay',
    title: 'Replay',
    type: 'replay',
  },
  comparer: {
    id: 'comparer',
    title: 'Comparer',
    type: 'comparer',
  },
  interact: {
    id: 'interact',
    title: 'Interact',
    type: 'interact',
  },
  'proxy-settings': {
    id: 'proxy-settings',
    title: 'TORCH Settings',
    type: 'proxy-settings',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
    type: 'settings',
  },
  'network-graph': {
    id: 'network-graph',
    title: 'Network Graph',
    type: 'network-graph',
  },
} satisfies Record<SidebarTab, OpenMainContentTabInput>;
export const getSidebarTabMapping = ({
  executions,
  applicationSettings,
  proxySettings,
  targetOverview,
  home,
  networkGraph,
  targetType,
}: Pick<
  SidebarLabels,
  | 'executions'
  | 'applicationSettings'
  | 'proxySettings'
  | 'targetOverview'
  | 'home'
  | 'networkGraph'
> & {
  targetType: TargetTypeType;
}): Record<SidebarTab, OpenMainContentTabInput> => ({
  ...sidebarTabMapping,
  dashboard: {
    ...sidebarTabMapping.dashboard,
    title: targetSupports({ targetType, capability: 'networkOverview' })
      ? targetOverview
      : home,
  },
  'agent-executions': {
    ...sidebarTabMapping['agent-executions'],
    title: executions,
  },
  settings: {
    ...sidebarTabMapping.settings,
    title: applicationSettings,
  },
  'proxy-settings': {
    ...sidebarTabMapping['proxy-settings'],
    title: proxySettings,
  },
  'network-graph': {
    ...sidebarTabMapping['network-graph'],
    title: targetSupports({ targetType, capability: 'networkGraph' })
      ? networkGraph
      : sidebarTabMapping['network-graph'].title,
  },
});
const buildTrafficSection = ({
  sectionTrafficAndMonitoring,
  httpHistory,
  webSocketSse,
}: Pick<
  SidebarSectionOptions,
  'sectionTrafficAndMonitoring' | 'httpHistory' | 'webSocketSse'
>): SidebarSection => ({
  id: 'traffic',
  sectionIcon: RadioIcon,
  sectionLabel: sectionTrafficAndMonitoring,
  items: [
    {
      id: 'requests',
      icon: WorkHistoryIcon,
      label: httpHistory,
      count: undefined,
    },
    {
      id: 'websocket',
      icon: CableIcon,
      label: webSocketSse,
      count: undefined,
    },
  ],
});
const buildProxyToolsSection = ({
  sectionProxyTools,
  intercept,
  matchReplace,
  replay,
  comparer,
  interact,
}: Pick<
  SidebarSectionOptions,
  | 'sectionProxyTools'
  | 'intercept'
  | 'matchReplace'
  | 'replay'
  | 'comparer'
  | 'interact'
>): SidebarSection => ({
  id: 'proxy',
  sectionIcon: FlashIcon,
  sectionLabel: sectionProxyTools,
  items: [
    {
      id: 'intercept',
      icon: FlashIcon,
      label: intercept,
    },
    {
      id: 'match-replace',
      icon: ReplaceIcon,
      label: matchReplace,
    },
    {
      id: 'replay',
      icon: ReplayIcon,
      label: replay,
    },
    {
      id: 'comparer',
      icon: GitCompareIcon,
      label: comparer,
    },
    {
      id: 'interact',
      icon: CursorPointer01Icon,
      label: interact,
    },
  ],
});

type DataSidebarItemDef = {
  id: SidebarTab;
  icon: TerraIcon;
  capability: TargetCapability;
  requiresExternalNetwork?: boolean;
  hasNew?: boolean;
  isSubItem?: boolean;
  getLabel: (labels: SidebarSectionOptions) => string;
};
const DATA_SIDEBAR_ITEM_DEFS: DataSidebarItemDef[] = [
  {
    id: 'dashboard',
    icon: Home03Icon,
    capability: 'networkOverview',
    getLabel: (labels) =>
      isAppTarget(labels.targetType) ? labels.home : labels.targetOverview,
  },
  {
    id: 'network-graph',
    icon: HierarchyIcon,
    capability: 'networkGraph',
    isSubItem: true,
    getLabel: (labels) => labels.networkGraph,
  },
  {
    id: 'assets',
    icon: Shapes01Icon,
    capability: 'assets',
    getLabel: (labels) => labels.assets,
  },
  {
    id: 'code',
    icon: SourceCodeIcon,
    capability: 'codeGraph',
    getLabel: (labels) => labels.code,
  },
  {
    id: 'signals',
    icon: Target03Icon,
    capability: 'signals',
    hasNew: false,
    getLabel: (labels) => labels.signals,
  },
  {
    id: 'executions',
    icon: PlayIcon,
    capability: 'executions',
    getLabel: (labels) => labels.executions,
  },
  {
    id: 'agent-executions',
    icon: PlayIcon,
    capability: 'agentExecutions',
    getLabel: (labels) => labels.executions,
  },
  {
    id: 'findings',
    icon: Alert01Icon,
    capability: 'findings',
    hasNew: false,
    getLabel: (labels) => labels.findings,
  },
  {
    id: 'reports',
    icon: File01Icon,
    capability: 'reports',
    getLabel: (labels) => labels.reports,
  },
];
const buildDataSection = ({
  labels,
  targetType,
  isExternalNetworkEnabled,
  hasApplicationSourceCode = true,
}: {
  labels: SidebarSectionOptions;
  targetType: TargetTypeType;
  isExternalNetworkEnabled: boolean;
  hasApplicationSourceCode?: boolean;
}): SidebarSection | null => {
  const items = DATA_SIDEBAR_ITEM_DEFS.filter((item) => {
    if (item.id === 'dashboard') {
      return (
        isAppTarget(targetType) ||
        targetSupports({ targetType, capability: item.capability })
      );
    }

    if (item.id === 'code' && !hasApplicationSourceCode) {
      return false;
    }

    return (
      targetSupports({ targetType, capability: item.capability }) &&
      (!item.requiresExternalNetwork || isExternalNetworkEnabled)
    );
  }).map((item) => ({
    id: item.id,
    icon: item.icon,
    label: item.getLabel(labels),
    count: undefined as number | undefined,
    ...(item.isSubItem ? { isSubItem: true } : {}),
    ...(item.hasNew !== undefined ? { hasNew: item.hasNew } : {}),
  }));

  if (items.length === 0) {
    return null;
  }

  return {
    id: 'data',
    sectionIcon: FolderLibraryIcon,
    sectionLabel: labels.sectionDataAndAssets,
    items,
  };
};

export const getSidebarSections = (
  options: SidebarSectionOptions,
): SidebarSection[] => {
  const {
    sectionTrafficAndMonitoring,
    sectionProxyTools,
    httpHistory,
    webSocketSse,
    intercept,
    matchReplace,
    replay,
    comparer,
    interact,
    targetType,
    isExternalNetworkEnabled,
  } = options;
  const sections: SidebarSection[] = [];
  const dataSection = buildDataSection({
    labels: options,
    targetType,
    isExternalNetworkEnabled,
    hasApplicationSourceCode: options.hasApplicationSourceCode,
  });

  if (dataSection) {
    sections.push(dataSection);
  }

  if (targetSupports({ targetType, capability: 'proxyTools' })) {
    sections.push(
      buildTrafficSection({
        sectionTrafficAndMonitoring,
        httpHistory,
        webSocketSse,
      }),
      buildProxyToolsSection({
        sectionProxyTools,
        intercept,
        matchReplace,
        replay,
        comparer,
        interact,
      }),
    );
  }

  return sections;
};
